import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Settings, Plus, Trash2, Edit2, Save, Loader2, 
  CheckSquare, FolderKanban, Ticket, Palette, GripVertical
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CATEGORY_CONFIG = {
  task: { label: 'Tâches', icon: CheckSquare, description: 'Statuts utilisés pour les tâches' },
  project: { label: 'Projets', icon: FolderKanban, description: 'Statuts utilisés pour les projets' },
  ticket: { label: 'Tickets', icon: Ticket, description: 'Statuts utilisés pour le support' }
};

const COLOR_PALETTE = [
  '#64748b', '#ef4444', '#f97316', '#eab308', '#22c55e', 
  '#14b8a6', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899'
];

const StatusSettingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [activeTab, setActiveTab] = useState('task');
  
  // Add/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [formData, setFormData] = useState({ key: '', label: '', color: '#3b82f6' });

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await api.get('/settings/statuses');
      setStatuses(response.data.statuses);
    } catch (error) {
      console.error('Error fetching statuses:', error);
      toast.error('Erreur lors du chargement des statuts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.key || !formData.label) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSaving(true);
    try {
      if (editingStatus) {
        // Update existing status
        const updatedStatuses = { ...statuses };
        const index = updatedStatuses[activeTab].findIndex(s => s.key === editingStatus.key);
        if (index !== -1) {
          updatedStatuses[activeTab][index] = formData;
        }
        await api.put('/settings/statuses', updatedStatuses);
        setStatuses(updatedStatuses);
        toast.success('Statut mis à jour');
      } else {
        // Add new status
        await api.post(`/settings/statuses/${activeTab}/add`, formData);
        await fetchStatuses();
        toast.success('Statut ajouté');
      }
      setShowModal(false);
      setEditingStatus(null);
      setFormData({ key: '', label: '', color: '#3b82f6' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    if (!confirm('Supprimer ce statut ?')) return;
    
    try {
      await api.delete(`/settings/statuses/${activeTab}/${key}`);
      await fetchStatuses();
      toast.success('Statut supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditModal = (status) => {
    setEditingStatus(status);
    setFormData({ ...status });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingStatus(null);
    setFormData({ key: '', label: '', color: '#3b82f6' });
    setShowModal(true);
  };

  const generateKey = (label) => {
    return label.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  };

  return (
    <div className="flex h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 flex-1 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Gestion des statuts
            </h1>
            <p className="text-slate-500 mt-1">
              Personnalisez les noms et couleurs des statuts
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="border-b px-6 pt-4">
                  <TabsList className="bg-transparent gap-4 h-auto p-0">
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <TabsTrigger
                          key={key}
                          value={key}
                          className={cn(
                            "flex items-center gap-2 px-4 py-2 rounded-t-lg border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:bg-transparent",
                            activeTab === key ? "border-indigo-500" : ""
                          )}
                          style={activeTab === key ? { borderColor: theme.colors?.accent } : {}}
                        >
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>

                {Object.entries(CATEGORY_CONFIG).map(([category, config]) => (
                  <TabsContent key={category} value={category} className="m-0">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-slate-500">{config.description}</p>
                        <Button onClick={openAddModal} size="sm" style={{ backgroundColor: theme.colors?.accent }}>
                          <Plus className="w-4 h-4 mr-1" />
                          Ajouter
                        </Button>
                      </div>

                      <div className="space-y-2">
                        {(statuses[category] || []).map((status, index) => (
                          <div
                            key={status.key}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              <GripVertical className="w-4 h-4 text-slate-300 cursor-grab" />
                              <div
                                className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                                style={{ backgroundColor: status.color }}
                              />
                              <div>
                                <p className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                                  {status.label}
                                </p>
                                <p className="text-xs text-slate-400 font-mono">{status.key}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Badge
                                className="text-xs"
                                style={{ backgroundColor: `${status.color}20`, color: status.color }}
                              >
                                Aperçu
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditModal(status)}
                              >
                                <Edit2 className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-red-50 hover:text-red-600"
                                onClick={() => handleDelete(status.key)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}

                        {(!statuses[category] || statuses[category].length === 0) && (
                          <div className="text-center py-8 text-slate-400">
                            <Settings className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>Aucun statut configuré</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingStatus ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {editingStatus ? 'Modifier le statut' : 'Ajouter un statut'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Libellé *</Label>
                <Input
                  value={formData.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    setFormData({ 
                      ...formData, 
                      label,
                      key: editingStatus ? formData.key : generateKey(label)
                    });
                  }}
                  placeholder="Ex: En attente de validation"
                />
              </div>

              <div className="space-y-2">
                <Label>Identifiant technique</Label>
                <Input
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="Ex: awaiting_validation"
                  disabled={!!editingStatus}
                  className={editingStatus ? 'bg-slate-100' : ''}
                />
                <p className="text-xs text-slate-500">
                  {editingStatus ? 'Non modifiable' : 'Généré automatiquement depuis le libellé'}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Couleur
                </Label>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2 flex-wrap">
                    {COLOR_PALETTE.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={cn(
                          "w-8 h-8 rounded-full border-2 transition-all",
                          formData.color === color ? "border-slate-800 scale-110" : "border-transparent hover:scale-105"
                        )}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({ ...formData, color })}
                      />
                    ))}
                  </div>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-8 p-1 cursor-pointer"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Aperçu</p>
                <Badge
                  className="text-sm px-3 py-1"
                  style={{ 
                    backgroundColor: `${formData.color}20`, 
                    color: formData.color,
                    border: `1px solid ${formData.color}40`
                  }}
                >
                  {formData.label || 'Libellé du statut'}
                </Badge>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving || !formData.key || !formData.label}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingStatus ? 'Mettre à jour' : 'Ajouter'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default StatusSettingsPage;
